/**
 * El QR que sustituye al correo de invitación.
 *
 * Se enseña UNA vez, al acabar el alta: el token no se puede volver a
 * consultar (ver `AdminUser.activation_token`). No se pinta en el navegador
 * con una librería de QR — no hay ninguna en `package.json` y añadirla
 * obligaría a un `npm ci` en Docker con el volumen `kakuat_node`, que en
 * este repo ya ha dado problemas. Lo genera el backend, que ya sabe hacerlo
 * para los centros de trabajo (`DynamicQRService`), y se pide como Blob con
 * la sesión del responsable — igual que `DocumentPreview` con los ficheros
 * de PRL, nunca un `<img src>` a pelo porque la ruta exige token.
 */
import { useEffect, useState } from 'react';
import { getActivationQr } from '../../services/apiClient';

type Props = {
  userId: number;
  /** El token del alta: el componente pide el PNG con él. Omitir si se pasa
   * `preloadedBlobUrl` (caso de reemisión, ver abajo). */
  token?: string;
  /** Cuando el Blob ya se tiene de antemano (reemisión: `WorkersAdmin`
   * llama a `reissueActivationQr` porque no hay ningún token que pasar) se
   * entrega aquí en vez de pedirlo. Su ciclo de vida —creación y
   * revocación— es de quien lo crea, no de este componente. */
  preloadedBlobUrl?: string;
  nombre: string;
  /** Encabezado; por defecto asume que es el momento del alta. */
  titulo?: string;
  /** Botón para volver a la lista una vez enseñado el QR. Opcional: el
   * componente en sí sólo pinta el QR e imprime. */
  onClose?: () => void;
};

export function ActivationQr({ userId, token, preloadedBlobUrl, nombre, titulo, onClose }: Props) {
  const [url, setUrl] = useState<string | null>(preloadedBlobUrl ?? null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (preloadedBlobUrl) {
      // Ya viene resuelto (reemisión): no se vuelve a pedir, y tampoco se
      // revoca aquí — lo revoca quien lo creó.
      setUrl(preloadedBlobUrl);
      return;
    }
    if (!token) return;

    setUrl(null);
    setError(null);
    let vivo = true;
    let creado: string | null = null;

    getActivationQr(userId, token)
      .then((blob) => {
        if (!vivo) return;
        creado = URL.createObjectURL(blob);
        setUrl(creado);
      })
      .catch(() => vivo && setError('No se pudo generar el QR'));

    return () => {
      vivo = false;
      // Si no se revoca, cada QR generado deja su copia en memoria hasta
      // que se cierre la pestaña.
      if (creado) URL.revokeObjectURL(creado);
    };
  }, [userId, token, preloadedBlobUrl]);

  return (
    <div className="card-surface p-6 text-center space-y-3">
      <p className="font-semibold text-[15.5px]">{titulo ?? `${nombre} ya está dado de alta`}</p>

      <div className="mx-auto w-[260px] h-[260px] rounded-xl bg-surface-2 grid place-items-center overflow-hidden">
        {error && <p className="text-[13.5px] text-danger px-4">{error}</p>}
        {!error && !url && <p className="text-[13.5px] text-ink-3">Generando…</p>}
        {url && <img src={url} alt="QR de activación" className="w-full h-full object-contain" />}
      </div>

      <p className="text-[13.5px] text-ink-3 max-w-[320px] mx-auto">
        Que lo escanee ahora con su móvil, delante de ti. Este código no se
        puede volver a ver: si se pierde, se reemite desde su ficha.
      </p>

      <p className="text-[13.5px] text-ink-3 max-w-[320px] mx-auto">
        Al escanearlo se le asigna un PIN, que es su credencial para el quiosco
        y para recuperar el acceso. Su pantalla lo enseña una sola vez:
        asegúrate de que lo apunta.
      </p>

      <div className="flex items-center justify-center gap-3 pt-1">
        <button
          onClick={() => window.print()}
          disabled={!url}
          className="h-11 px-5 rounded-xl border border-line-strong font-semibold text-[14px] disabled:opacity-40"
        >
          Imprimir
        </button>
        {onClose && (
          <button
            onClick={onClose}
            className="h-11 px-5 rounded-xl bg-brand text-on-brand font-semibold text-[14px]"
          >
            Volver a la lista
          </button>
        )}
      </div>
    </div>
  );
}
